import { useEffect, useState, useContext, useCallback } from "react";
import { Col, Container, Row, Hidden } from "react-grid-system";
import { Route, Switch, Redirect } from "react-router-dom";
import styled from "styled-components";
import { debounce } from "lodash";

import { MainWrapper } from "../../../atoms/containers/main-wrapper";
import { LetterPreview, ILetterPreviewUpdateArgs } from "../../../organisms/letter-preview";
import { MobileOverlay } from "../../../organisms/mobile-overlay";
import { ShowNavContext } from "../../../contexts/show-nav-context";
import { useLetterFormData } from "../../../../hooks/letter/use-letter-form-data";
import { DataLoadedContainer } from "../../../control/data-loaded-container";
import { LetterPreviewPlaceholder } from "../../../atoms/letter-preview-placeholder";
import { routePaths } from "../../../../model/route";
import { CallOutPanel } from "../../../atoms/callout-panel";

import { SantaLetterDetailsContent } from "./santa-letter-details-content";
import { SantaLetterDetailsSending } from "./santa-letter-details-sending";

const PreviewPlaceholder = styled(LetterPreviewPlaceholder)`
  margin-top: 30px;
`;

const MobilePreviewContainer = styled.div`
  canvas {
    width: 100vw;
  }
`;

const PreviewWrapper = styled.div`
  position: sticky;
  top: 0;

  canvas {
    width: 100%;
  }
`;

const StyledCallOutPanel = styled(CallOutPanel)`
  margin-top: 16px;
`;

const TruncationWarning = (): JSX.Element => (
  <StyledCallOutPanel noDecoration>
    This is a partial preview of your personalised Santa letter. To protect our magical content, the
    full letter is available after purchase. Don&apos;t worry — you&apos;ll have full access to read
    and edit the complete letter before it&apos;s dispatched.
  </StyledCallOutPanel>
);

const truncateParagraphsToWordLimit = (paragraphs: string[], wordLimit: number): string[] => {
  const result: string[] = [];
  let totalWords = 0;

  for (const paragraph of paragraphs) {
    const words = paragraph.split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;

    if (totalWords + wordCount <= wordLimit) {
      result.push(paragraph);
      totalWords += wordCount;
    } else {
      // Truncate the final paragraph to fit within the limit
      const remainingWords = wordLimit - totalWords;
      if (remainingWords > 0) {
        const truncatedParagraph = words.slice(0, remainingWords).join(" ");
        result.push(truncatedParagraph);
      }
      break;
    }
  }

  return result;
};

export const SantaLetterDetails: React.FC = () => {
  const { hideNav, showNav } = useContext(ShowNavContext);
  const [shouldShowMobilePreview, setShouldShowMobilePreview] = useState(false);
  const [previewProps, setPreviewProps] = useState<ILetterPreviewUpdateArgs>();
  const { data, isLoading, refetch, getPreview } = useLetterFormData();

  const updatePreview = async (): Promise<void> => {
    const props = await getPreview();
    if (props) {
      // hide some content for this view
      props.signature = undefined;
      props.paragraphs = truncateParagraphsToWordLimit(props.paragraphs, 100);
      props.fadeBottom = true;
    }

    setPreviewProps(props);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updatePreview();
  }, [getPreview]);

  const handleUpdateDetails = useCallback(updatePreview, [getPreview]);

  const handleShowMobilePreview = useCallback((): void => {
    setShouldShowMobilePreview(true);
    hideNav();
  }, [setShouldShowMobilePreview, hideNav]);

  const handleHideMobilePreview = useCallback((): void => {
    setShouldShowMobilePreview(false);
    showNav();
  }, [setShouldShowMobilePreview, showNav]);

  const sendingForm = useCallback(
    (): JSX.Element => (
      <SantaLetterDetailsSending
        internationalSurcharge={data.internationalSurcharge}
        onClickOpenMobilePreview={handleShowMobilePreview}
        updateSendingDetails={debounce(handleUpdateDetails, 500)}
      />
    ),
    [handleShowMobilePreview, handleUpdateDetails, data.internationalSurcharge],
  );

  const contentForm = useCallback(
    (): JSX.Element => (
      <SantaLetterDetailsContent
        templateOptions={data.templates}
        psOptions={data.ps}
        signatureOptions={data.signatures}
        petOptions={data.pets}
        frontDoorOptions={data.frontDoors}
        onClickOpenMobilePreview={handleShowMobilePreview}
        onChange={debounce(handleUpdateDetails, 500)}
      />
    ),
    [
      handleShowMobilePreview,
      handleUpdateDetails,
      data.templates,
      data.ps,
      data.signatures,
      data.pets,
      data.frontDoors,
    ],
  );

  return (
    <DataLoadedContainer isLoading={isLoading} refetch={refetch}>
      <MainWrapper>
        <Container>
          <Row>
            <Col md={6}>
              <Switch>
                <Route exact path={routePaths.letter.recipient} render={sendingForm} />
                <Route exact path={routePaths.letter.content} render={contentForm} />
                <Redirect to={routePaths.letter.types.classic} />
              </Switch>
            </Col>
            <Hidden xs>
              <Col md={6}>
                <PreviewWrapper>
                  {previewProps ? (
                    <>
                      <LetterPreview params={previewProps} />
                      <TruncationWarning />
                    </>
                  ) : (
                    <PreviewPlaceholder />
                  )}
                </PreviewWrapper>
              </Col>
            </Hidden>
          </Row>
        </Container>
        {shouldShowMobilePreview && previewProps && (
          <MobileOverlay onClickClose={handleHideMobilePreview}>
            <MobilePreviewContainer>
              <LetterPreview params={previewProps} />
              <TruncationWarning />
            </MobilePreviewContainer>
          </MobileOverlay>
        )}
      </MainWrapper>
    </DataLoadedContainer>
  );
};
